/* Naughty Hut mobile tab bar (<990px): Home · Shop · Match · Bag.
 * Always on screen (tab bar 2026-09-28): it only steps aside while a drawer, modal or the
 * cookie banner is open, and turns slightly compact while the shopper scrolls down.
 * Keeps the Bag count live, and sits above the Shopify preview bar when that bar is present,
 * so the owner reviewing in preview sees what customers see. */
(() => {
  'use strict';

  /* ---------- site chrome: pause looping animations while off-screen ----------
   * The announcement marquee, footer hearts and the satin/heartbeat loops on the
   * home and quiz pages run forever. Toggle .nh-anim-off (nh-hot-header.css) so
   * they only animate while visible. Reduced motion is handled in CSS. */
  if ('IntersectionObserver' in window) {
    const loops = document.querySelectorAll('.component-marquee-container, .nhf-news, .nhi-beat, .nhi-join__text, .nhi-quizhero, .nhi-beatpulse, .nhq__sparkles, .nhq, .nhi-swipe');
    if (loops.length) {
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) e.target.classList.toggle('nh-anim-off', !e.isIntersecting);
      });
      for (const el of loops) io.observe(el);
    }
  }

  const dock = document.getElementById('NhDock');
  if (!dock) return;
  const root = document.documentElement;
  const mq = window.matchMedia('(max-width: 989px)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const badge = dock.querySelector('[data-nhk-count]');
  const sr = dock.querySelector('[data-nhk-sr]');

  /* ---------- actions ---------- */
  dock.querySelector('[data-nhk-shop]')?.addEventListener('click', (e) => {
    const trigger = document.querySelector('[data-nhd-open]');
    if (!trigger) return; // no drawer: follow the link
    e.preventDefault();
    trigger.click();
  });
  dock.querySelector('[data-nhk-bag]')?.addEventListener('click', (e) => {
    const trigger = document.querySelector('.js-drawer-open-cart');
    if (!trigger || !document.getElementById('CartDrawer')) return; // cart page fallback
    e.preventDefault();
    e.stopPropagation(); // the theme closes drawers on outside clicks; don't let this click reach it
    setTimeout(() => trigger.click(), 0); // same path as the header bag icon
  });

  /* ---------- bag count ---------- */
  let count = Number(badge?.textContent) || 0;
  const setCount = (n) => {
    n = Number(n) || 0;
    if (badge) {
      badge.textContent = n > 99 ? '99+' : String(n);
      badge.hidden = n === 0;
      if (n > count && !reduce.matches) {
        badge.classList.remove('is-bump');
        void badge.offsetWidth; // restart the bump
        badge.classList.add('is-bump');
      }
    }
    if (sr) sr.textContent = `, ${n} ${n === 1 ? 'item' : 'items'}`;
    count = n;
  };
  let refreshTimer = 0;
  const refresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then((r) => r.json()).then((c) => setCount(c.item_count)).catch(() => {});
    }, 150);
  };
  document.addEventListener('cart:updated', (e) => { const c = e.detail && e.detail.cart; if (c && typeof c.item_count === 'number') setCount(c.item_count); else refresh(); });
  document.addEventListener('ajaxProduct:added', refresh);
  document.addEventListener('cart:change', refresh);
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });
  // Any add/change/clear request from any script (theme, quick add, apps) re-reads /cart.js once it finishes.
  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver((list) => {
        if (list.getEntries().some((en) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(new URL(en.name, location.href).pathname + '?'))) refresh();
      }).observe({ type: 'resource', buffered: false });
    } catch (err) { /* older browsers: the theme events above still apply */ }
  }

  /* ---------- Shopify preview bar: lift the tab bar above it ----------
   * The preview bar (#PBarNextFrameWrapper, older #preview-bar-iframe) is a bottom-anchored
   * top-layer element, so no z-index can beat it. Customers never have it; when it is there we
   * move the tab bar (and everything stacked on it) up by its height via --nhk-lift. */
  const PB_SEL = '#PBarNextFrameWrapper, #preview-bar-iframe';
  let pbEl = null;
  let pbRO = null;
  const measureBar = () => {
    const el = document.querySelector(PB_SEL);
    if (el !== pbEl) {
      pbRO?.disconnect();
      pbEl = el;
      if (el && 'ResizeObserver' in window) { pbRO = new ResizeObserver(() => measureBar()); pbRO.observe(el); }
    }
    let lift = 0;
    if (el && el.isConnected) {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // only a bar docked to the bottom edge counts, and never more than a bar's worth
      if (r.height > 0 && r.width > 0 && r.bottom >= vh - 4 && r.top > vh * 0.5) lift = Math.min(Math.round(vh - r.top), 140);
    }
    root.style.setProperty('--nhk-lift', `${lift}px`);
    root.classList.toggle('nhk-pb', lift > 0);
  };
  measureBar();
  new MutationObserver((ms) => {
    if (ms.some((m) => [...m.addedNodes, ...m.removedNodes].some((n) => n.nodeType === 1 && (n.matches(PB_SEL) || n.querySelector?.(PB_SEL))))) measureBar();
  }).observe(document.body, { childList: true });
  window.addEventListener('resize', measureBar, { passive: true });
  window.addEventListener('load', measureBar);

  /* ---------- visibility: only overlays hide it ---------- */
  const OVERLAYS = [
    '#NhDrawer.is-open', '#CartDrawer.drawer--is-open', '.drawer.drawer--is-open',
    'predictive-search.nhps-open', '.modal.modal--is-active', '[role="dialog"][aria-modal="true"]:not([hidden]):not(#NhDrawer)',
    '.collection-filter.is-open', 'html.js-drawer-open', 'html.nhd-lock', 'html.nhfd-lock', 'dialog[open]', '#shopify-pc__banner',
  ];
  const overlayOpen = () => OVERLAYS.some((s) => {
    const el = document.querySelector(s);
    if (!el) return false;
    if (el === root) return true;
    const r = el.getBoundingClientRect();
    const cs = getComputedStyle(el);
    return r.width > 0 && r.height > 0 && cs.visibility !== 'hidden' && cs.display !== 'none' && cs.opacity !== '0';
  });
  /* ---------- keep primary controls clear (QA fix A: DQA-02 / XB-03) ----------
   * While the collection Filter/Sort bar or the home hero buttons sit in the band the dock covers, the dock
   * steps aside, so a tap on Filter or Sort never lands on Home or Match. It comes back as soon as they
   * scroll clear (the toolbar sticks to the top, the hero scrolls away). Two rect reads per frame at most. */
  const KEEP_CLEAR = '[data-nhp-toolbar], .nhv-ctas, .nhi-hero__ctas';
  const keepClear = [...document.querySelectorAll(KEEP_CLEAR)];
  const bandBlocked = () => {
    if (!keepClear.length) return false;
    const vh = window.innerHeight;
    const top = vh - (parseFloat(getComputedStyle(dock).bottom) || 0) - dock.offsetHeight - 8;
    return keepClear.some((el) => {
      const r = el.getBoundingClientRect();
      return r.height > 0 && r.bottom > top && r.top < vh;
    });
  };
  let lastHide = null;
  const update = () => {
    if (!mq.matches) { if (!dock.hidden) dock.hidden = true; return; }
    if (dock.hidden) dock.hidden = false;
    const hide = overlayOpen() || bandBlocked();
    if (hide === lastHide) return; // only write when something changed (avoids observer ping-pong)
    lastHide = hide;
    dock.classList.toggle('is-hidden', hide);
    root.classList.toggle('nhk-away', hide);
    if (hide) dock.setAttribute('inert', ''); else dock.removeAttribute('inert');
  };
  // batch observer-driven checks into one per frame; ignore the dock's own mutations
  let queued = false;
  const schedule = () => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; update(); }); };

  /* ---------- compact while scrolling down (never hidden) ---------- */
  let lastY = window.scrollY;
  let ticking = false;
  const COMPACT_FROM = 160;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      if (y < COMPACT_FROM) root.classList.remove('nhk-compact');
      else if (Math.abs(y - lastY) > 8) root.classList.toggle('nhk-compact', y > lastY);
      if (Math.abs(y - lastY) > 8) lastY = y;
      ticking = false;
      if (keepClear.length) update();
    });
  }, { passive: true });

  /* Only overlay-shaped mutations re-check the dock, so busy pages (hero rotation, reveals) never
     force a layout read per frame. */
  const OVERLAY_HOST = '#NhDrawer, #CartDrawer, .drawer, predictive-search, .modal, [role="dialog"], [aria-modal], .collection-filter, dialog, #shopify-pc__banner';
  const relevant = (m) => {
    const t = m.target;
    if (!t || t.nodeType !== 1 || dock.contains(t)) return false;
    if (m.type === 'childList') return t === document.body || [...m.addedNodes, ...m.removedNodes].some((n) => n.nodeType === 1 && (n.matches(OVERLAY_HOST) || !!n.querySelector?.('[role="dialog"], [aria-modal], dialog, #shopify-pc__banner')));
    return t.matches(OVERLAY_HOST);
  };
  new MutationObserver((ms) => { if (ms.some(relevant)) schedule(); }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'hidden', 'aria-modal', 'open', 'style'] });
  new MutationObserver(schedule).observe(root, { attributes: true, attributeFilter: ['class'] });
  root.classList.add('nhk-on');
  mq.addEventListener?.('change', () => { lastHide = null; update(); measureBar(); });
  if (reduce.matches) dock.classList.add('is-static');
  if (keepClear.length) { window.addEventListener('resize', schedule, { passive: true }); window.addEventListener('load', schedule); }
  update();

  /* Fix batch 2026-09-29 (FE-F05 / D-41): when the Klaviyo popup is closed by tap or mouse it hands focus
     to whatever control sat under it (a dock tab, a menu item, an FAQ question), which then wore a focus ring on every first visit and read as a selected tab. Drop
     that stray focus unless the shopper is using the keyboard. */
  let lastKey = 0;
  document.addEventListener('keydown', () => { lastKey = performance.now(); }, true);
  const dropStray = () => setTimeout(() => {
    const a = document.activeElement;
    if (a && a !== document.body && performance.now() - lastKey > 1500) a.blur();
  }, 80);
  window.addEventListener('klaviyoForms', (e) => { if (e.detail && e.detail.type === 'close') dropStray(); });
})();
